"use client"

import { isPublicSubscribePath } from "@/app/subscribe/utils"
import { isLegacySystemPath } from "@/app/legacy-system/utils"

const RING_SESSION_COOKIE_NAME = "ring_session"
const AUTH_SESSION_COOKIE_NAMES = new Set([RING_SESSION_COOKIE_NAME, "launcher-id", "JSESSIONID"])

interface SessionPayloadLike {
  authenticated?: unknown
}

interface CookieChangeEventLike {
  changed?: Array<{ name?: unknown }>
  deleted?: Array<{ name?: unknown }>
}

function hasAuthSessionCookie(cookies: Array<{ name?: unknown }> | undefined, { includeRingSessionChanges }: { includeRingSessionChanges: boolean }) {
  return Boolean(
    cookies?.some((cookie) => {
      if (typeof cookie.name !== "string") {
        return false
      }

      if (!includeRingSessionChanges && cookie.name === RING_SESSION_COOKIE_NAME) {
        return false
      }

      return AUTH_SESSION_COOKIE_NAMES.has(cookie.name)
    })
  )
}

export function isAuthenticatedSessionPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") {
    return false
  }

  return (payload as SessionPayloadLike).authenticated === true
}

export function shouldCheckClientAuthSession(pathname: string | null): boolean {
  if (!pathname) {
    return true
  }

  return !pathname.startsWith("/login") && !pathname.startsWith("/recover-password") && !pathname.startsWith("/sign-up") && !isPublicSubscribePath(pathname) && !isLegacySystemPath(pathname)
}

export function isClientAuthSessionLost(isAuthenticated: boolean | null): boolean {
  return isAuthenticated === false
}

export function shouldCheckClientAuthSessionCookieEvent(event: Event): boolean {
  const cookieEvent = event as CookieChangeEventLike

  return (
    hasAuthSessionCookie(cookieEvent.deleted, {
      includeRingSessionChanges: true,
    }) ||
    hasAuthSessionCookie(cookieEvent.changed, {
      includeRingSessionChanges: false,
    })
  )
}

export async function fetchCurrentClientAuthSession(): Promise<boolean | null> {
  try {
    const response = await fetch("/api/session", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    })

    if (!response.ok) {
      return null
    }

    return isAuthenticatedSessionPayload(await response.json())
  } catch {
    return null
  }
}
